import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import useAuth from '../hooks/useAuth';
import { useSeason } from '../context/SeasonContext';

export default function Users() {
  const { organiserRegister, authError, setAuthError } = useAuth();
  const [form, setForm] = useState({
    name: '', email: '', mobile: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [localError, setLocalError] = useState('');
  const { selectedSeason } = useSeason();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedSeason) return;
      setLoadingUsers(true);
      try {
        const token = sessionStorage.getItem("maaze_token");
        const response = await axios.get(
          `https://maize.norissolutions.in/Php/public/api/users?season_id=${selectedSeason}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response.data?.data;
        if (Array.isArray(data)) {
          const organisers = data.filter(u => u.role === "Organiser");
          setUsers(organisers);
        } else {
          setUsers([]);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [selectedSeason]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setLocalError('');
    if (authError) setAuthError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setMessage('');

    if (!selectedSeason) {
      setLocalError("Please select a season from top bar");
      return;
    }

    setLoading(true);

    const result = await organiserRegister({
      name: form.name,
      email: form.email,
      mobile: form.mobile,
      season_id: selectedSeason   // 🔥 from context
    });

    setLoading(false);

    if (result.success) {
      setMessage(`Organiser created for selected season`);
      setForm({ name: '', email: '', mobile: '' });
      
      // Optionally re-fetch after creation
      try {
        const token = sessionStorage.getItem("maaze_token");
        const response = await axios.get(
          `https://maize.norissolutions.in/Php/public/api/users?season_id=${selectedSeason}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response.data?.data;
        if (Array.isArray(data)) {
          setUsers(data.filter(u => u.role === "Organiser"));
        }
      } catch (e) { console.warn(e); }
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Create system users and access roles"
      />

      <div className="grid-1" style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Card title="Create New User">
          {message && <div style={{ color: 'var(--success-color)', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>{message}</div>}
          {(localError || authError) && <div style={{ color: 'var(--danger-color)', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>{localError || authError}</div>}

          <form onSubmit={handleSubmit} className="form-grid--double" style={{ gap: '1.5rem' }}>
            <FormField
              label="Full Name"
              type="text"
              name="name"
              placeholder="Enter full name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <FormField
              label="Email Address"
              type="email"
              name="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />

            <FormField
              label="Mobile Number"
              type="tel"
              name="mobile"
              placeholder="10-digit mobile number"
              value={form.mobile}
              onChange={handleChange}
              required
            />

            <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
              <Button
                type="submit"
                disabled={loading}
                style={{ width: '50%', display: 'block', margin: '0 auto' }}
              >
                {loading ? 'Creating...' : 'Create User Account'}
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Organisers List">
          {loadingUsers ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading organisers...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--surface-50)' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Name</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Email</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Mobile</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((u) => (
                      <tr key={u.user_id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{u.name}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{u.email}</td>
                        <td style={{ padding: '12px 16px' }}>{u.mobile}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ 
                            background: 'var(--accent-muted)', 
                            color: 'var(--accent)', 
                            padding: '4px 10px', 
                            borderRadius: '12px', 
                            fontSize: '12px',
                            fontWeight: 600 
                          }}>
                            {u.role}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        No organisers found for the selected season.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
